import { DEFAULT_CUSTOM_RECIPE } from '../services/customRecipe'

export type ThemeTokens = Record<string, string>

export type ThemeMode = 'preset' | 'generated'
export type ThemeGenerationBase = 'canvas-and-ink' | 'canvas-only'
export type BuiltInPaletteRecipeId = 'ink-wash' | 'tonal' | 'analogous' | 'split-complement' | 'triadic' | 'golden-angle'
export type PaletteRecipeId = BuiltInPaletteRecipeId | 'custom'
export type ThemeContrast = 'standard' | 'high'

export interface ThemeSettings {
  mode: ThemeMode
  preset: string
  generationBase: ThemeGenerationBase
  recipe: PaletteRecipeId
  customRecipe: string
  contrast: ThemeContrast
  overrides: ThemeTokens
}

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  mode: 'preset',
  preset: 'default',
  generationBase: 'canvas-and-ink',
  recipe: 'ink-wash',
  customRecipe: DEFAULT_CUSTOM_RECIPE,
  contrast: 'standard',
  overrides: {},
}

export interface ThemePreset {
  id: string
  name: string
  /** The page this theme is designed around. Choosing the theme moves the
   * page to these colors, unless the writer has set their own. */
  page: { bg: string; ink: string }
  tokens: ThemeTokens
}

/** Saved settings from before the presets were renamed. */
export const LEGACY_PRESET_IDS: Record<string, string> = {
  dark: 'default',
  light: 'night',
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default',
    name: 'Default',
    page: { bg: '#f0e3c4', ink: '#2b2115' },
    tokens: {
      // Brand — primary accent (buttons, links, active states)
      '--accent': '#ff6b45',
      '--accent-rgb': '255, 107, 69',
      '--accent-link': '#ffab8a',
      '--accent-contrast': '#2b2115',
      '--accent-light': '#ffab8a',
      '--accent-hover': '#ff8a63',
      '--accent-active': '#e65832',

      // Brand — secondary accent (toolbars, "connected" badges, etc.)
      '--secondary': '#1f7a72',
      '--secondary-rgb': '31, 122, 114',
      '--secondary-contrast': '#ffffff',

      // Surfaces — app background and elevated panels
      '--surface-bg': '#123f3a',
      '--surface-raised': '#1a4b45',
      '--surface-border': 'rgba(0, 0, 0, 0.3)',
      '--panel-bg': '#1a4b45',
      '--panel-text': '#f0e3c4',
      '--panel-muted': '#c5baa0',
      '--panel-border': '#91a9a4',
      '--panel-hover-bg': '#315e58',
      '--panel-control-bg': '#2a5751',
      '--panel-accent': '#ffab8a',
      '--panel-accent-rgb': '255, 171, 138',
      '--panel-active-bg': '#f0e3c4',
      '--panel-active-text': '#123f3a',
      '--control-bg': '#1a4b45',
      '--control-text': '#f0e3c4',
      '--control-border': '#8ba7a0',
      '--color-scheme': 'dark',

      // Text
      '--text-bright': '#f0e3c4',
      '--text-bright-rgb': '240, 227, 196',
      '--text-normal': 'rgba(240, 227, 196, 0.85)',
      '--text-muted': 'rgba(240, 227, 196, 0.65)',
      '--text-faint': '#666666',

      // Structure
      '--divider': 'rgba(240, 227, 196, 0.25)',

      // Status
      '--error': '#f87171',
      '--error-light': '#fca5a5',
      '--error-rgb': '248, 113, 113',
      '--success': '#34d399',
      '--success-rgb': '52, 211, 153',
      '--warning': '#fbbf24',
      '--warning-light': '#fef08a',
      '--warning-rgb': '251, 191, 36',

      // Version history diff (added / removed text)
      '--diff-added': '#7ee7d9',
      '--diff-added-rgb': '126, 231, 217',
      '--diff-removed': '#fd8c7e',
      '--diff-removed-rgb': '253, 140, 126',

      // Buttons
      '--button-hover-bg': 'rgba(255, 255, 255, 0.16)',
      '--button-active-bg': 'var(--text-bright)',
      '--button-active-text': 'var(--surface-bg)',

      // Toolbars / bars (top bar, bottom bar, right bar)
      '--toolbar-bg': 'var(--secondary)',
      '--toolbar-border': 'rgba(0, 0, 0, 0.3)',
      '--toolbar-shadow': '0 10px 24px rgba(var(--shadow-rgb), 0.28)',

      // Elevation (drop shadows on modals, dropdowns, toolbars)
      '--shadow-rgb': '20, 16, 10',
      '--surface-shadow': '0 15px 35px rgba(var(--shadow-rgb), 0.45)',

      // Form fields
      '--input-bg': 'rgba(var(--white-rgb), 0.05)',

      // Generic overlay tints (for translucent hover/border effects)
      '--white-rgb': '255, 255, 255',
      '--black-rgb': '0, 0, 0',
    },
  },
{
  // Late-night writing: warm graphite and parchment, lit by an amber lamp.
  // The typewriter teal stays on, deepened, as the brand color.
  id: 'night',
  name: 'Dark',
  page: { bg: '#1c1915', ink: '#ddd3bf' },
  tokens: {
    // Brand — primary accent (buttons, links, active states)
    '--accent': '#e8a54b',
    '--accent-rgb': '232, 165, 75',
    '--accent-link': '#f0c07a',
    '--accent-contrast': '#1a140a',
    '--accent-light': '#f0c07a',
    '--accent-hover': '#efb562',
    '--accent-active': '#cf8e35',

    // Brand — secondary (the settings sidebar, tool stage and badges; Type
    // Guide marks are lightened from it until they read on the page)
    '--secondary': '#2a5c55',
    '--secondary-rgb': '42, 92, 85',
    '--secondary-contrast': '#efe6d2',

    // Surfaces — app background and elevated panels
    '--surface-bg': '#15130f',
    '--surface-raised': '#1f1c17',
    '--surface-border': '#38332b',
    '--panel-bg': '#1b1915',
    '--panel-text': '#e8dfcc',
    '--panel-muted': '#aaa18f',
    '--panel-border': '#5a5348',
    '--panel-hover-bg': '#2a261f',
    '--panel-control-bg': '#24211b',
    '--panel-accent': '#e8a54b',
    '--panel-accent-rgb': '232, 165, 75',
    '--panel-active-bg': '#e8dfcc',
    '--panel-active-text': '#15130f',
    '--control-bg': '#1f1c17',
    '--control-text': '#e8dfcc',
    '--control-border': '#7a7264',
    '--color-scheme': 'dark',

    // Text
    '--text-bright': '#efe6d2',
    '--text-bright-rgb': '239, 230, 210',
    '--text-normal': 'rgba(239, 230, 210, 0.86)',
    '--text-muted': 'rgba(239, 230, 210, 0.64)',
    '--text-faint': '#6e675b',

    // Structure
    '--divider': 'rgba(239, 230, 210, 0.16)',

    // Status
    '--error': '#f0877a',
    '--error-light': '#f6b0a6',
    '--error-rgb': '240, 135, 122',
    '--success': '#7fcf9a',
    '--success-rgb': '127, 207, 154',
    '--warning': '#e8b84b',
    '--warning-light': '#f3d58a',
    '--warning-rgb': '232, 184, 75',

    // Version history diff (added / removed text)
    '--diff-added': '#8fd6c9',
    '--diff-added-rgb': '143, 214, 201',
    '--diff-removed': '#f29a8c',
    '--diff-removed-rgb': '242, 154, 140',

    // Buttons: an active key is lit amber
    '--button-hover-bg': 'rgba(255, 255, 255, 0.08)',
    '--button-active-bg': 'var(--accent)',
    '--button-active-text': 'var(--accent-contrast)',

    // Toolbars / bars: graphite, a step up from the surface
    '--toolbar-bg': '#25211b',
    '--toolbar-border': 'rgba(0, 0, 0, 0.55)',
    '--toolbar-shadow': '0 10px 24px rgba(var(--shadow-rgb), 0.45)',

    // Elevation (drop shadows on modals, dropdowns, toolbars)
    '--shadow-rgb': '0, 0, 0',
    '--surface-shadow': '0 15px 35px rgba(var(--shadow-rgb), 0.55)',

    // Form fields
    '--input-bg': 'rgba(var(--white-rgb), 0.05)',

    // Generic overlay tints (for translucent hover/border effects)
    '--white-rgb': '255, 255, 255',
    '--black-rgb': '0, 0, 0',
  },
},
{
  // Paper and ink: white and black with hard edges. Every pairing of text
  // and the color behind it reaches 7:1.
  id: 'high-contrast',
  name: 'High Contrast',
  page: { bg: '#ffffff', ink: '#000000' },
  tokens: {
    // Brand — primary accent (buttons, links, active states)
    '--accent': '#0047ab',
    '--accent-rgb': '0, 71, 171',
    '--accent-link': '#003a8c',
    '--accent-contrast': '#ffffff',
    '--accent-light': '#0047ab',
    '--accent-hover': '#003a8c',
    '--accent-active': '#002f72',

    // Brand — secondary (the settings sidebar, tool stage, badges, marks)
    '--secondary': '#005c53',
    '--secondary-rgb': '0, 92, 83',
    '--secondary-contrast': '#ffffff',

    // Surfaces — app background and elevated panels
    '--surface-bg': '#ffffff',
    '--surface-raised': '#ffffff',
    '--surface-border': '#000000',
    '--panel-bg': '#f2f2f0',
    '--panel-text': '#000000',
    '--panel-muted': '#333333',
    '--panel-border': '#000000',
    '--panel-hover-bg': '#dededa',
    '--panel-control-bg': '#ffffff',
    '--panel-accent': '#005c53',
    '--panel-accent-rgb': '0, 92, 83',
    '--panel-active-bg': '#000000',
    '--panel-active-text': '#ffffff',
    '--control-bg': '#ffffff',
    '--control-text': '#000000',
    '--control-border': '#000000',
    '--color-scheme': 'light',

    // Text
    '--text-bright': '#000000',
    '--text-bright-rgb': '0, 0, 0',
    '--text-normal': '#111111',
    '--text-muted': '#333333',
    '--text-faint': '#595959',

    // Structure
    '--divider': 'rgba(0, 0, 0, 0.55)',

    // Status
    '--error': '#a3001b',
    '--error-light': '#a3001b',
    '--error-rgb': '163, 0, 27',
    '--success': '#00632a',
    '--success-rgb': '0, 99, 42',
    '--warning': '#7a4a00',
    '--warning-light': '#7a4a00',
    '--warning-rgb': '122, 74, 0',

    // Version history diff (added / removed text)
    '--diff-added': '#005c53',
    '--diff-added-rgb': '0, 92, 83',
    '--diff-removed': '#a3001b',
    '--diff-removed-rgb': '163, 0, 27',

    // Buttons: an active key is solid black
    '--button-hover-bg': 'rgba(0, 0, 0, 0.1)',
    '--button-active-bg': '#000000',
    '--button-active-text': '#ffffff',

    // Toolbars / bars: white, outlined in black
    '--toolbar-bg': '#ffffff',
    '--toolbar-border': '#000000',
    '--toolbar-shadow': '0 8px 20px rgba(var(--shadow-rgb), 0.22)',

    // Elevation (drop shadows on modals, dropdowns, toolbars)
    '--shadow-rgb': '0, 0, 0',
    '--surface-shadow': '0 12px 30px rgba(var(--shadow-rgb), 0.25)',

    // Form fields
    '--input-bg': '#ffffff',

    // Generic overlay tints (for translucent hover/border effects)
    '--white-rgb': '255, 255, 255',
    '--black-rgb': '0, 0, 0',
  },
},
]
